import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Badge, Can, formatDateTime, type BadgeTone } from '@/kit/ui'
import { compareVersions } from '@/lib/batch'
import { FLOW_STATUS_LABEL, batchLabel, isFlowEditable } from '@/lib/flow'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useCodeStore } from '@/store/codeStore'
import { useFlowStore } from '@/store/flowStore'
import type { FlowStatus, Lender, WorkflowVersion } from '@/types'
import { workflowUrl } from '../links'
import { NewWorkflowDrawer } from './NewWorkflowDrawer'

const TONE: Record<FlowStatus, BadgeTone> = { draft: 'warning', paths_passed: 'info', live: 'success', previous: 'gray', retired: 'gray' }
export const FlowStatusBadge = ({ status }: { status: FlowStatus }) => <Badge tone={TONE[status]}>{FLOW_STATUS_LABEL[status]}</Badge>
const shortName = (name: string) => name.split('.').slice(1).join('.')

/**
 * 3.4 Workflows (F3-2): the version table. Open / View puts one version on the canvas, on its own page
 * (WorkflowEditor, /lenders/:slug/workflows/:id); New workflow and New version from this go straight there.
 */
export function WorkflowsSubTab({ lender }: { lender: Lender }) {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const flows = useFlowStore((s) => s.flows)
  const endpoints = useFlowStore((s) => s.endpoints)
  const batches = useCodeStore((s) => s.batches)
  const store = useFlowStore.getState
  const [creating, setCreating] = useState(false)

  const mine = useMemo(() => flows.filter((f) => f.lenderSlug === lender.slug).sort((a, b) => a.name.localeCompare(b.name) || compareVersions(b.version, a.version)), [flows, lender.slug])
  const behind = (f: WorkflowVersion) => endpoints.filter((e) => e.workflowName === f.name && e.workflowVersion === f.version && e.state !== 'retired').map((e) => e.id)
  const open = (f: WorkflowVersion) => navigate(workflowUrl(lender.slug, f.id))

  return (
    <>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-3xl text-xs text-muted">One workflow = one endpoint. One version runs behind it; switch in one click (3.5). <b>Open</b> puts a version on the canvas, on its own page: Save → Run all paths → Make live; live = read-only, so <b>New version from this</b>.</p>
        <Can permission="code.edit"><button type="button" className="btn btn-primary" onClick={() => setCreating(true)} data-testid="new-workflow">+ New workflow</button></Can>
      </div>
      {mine.length === 0 ? (
        <div className="card text-xs text-muted">No workflow yet for {lender.name}: + New workflow.</div>
      ) : (
        <div className="card-flush mb-3 overflow-x-auto">
          <table className="table">
            <thead><tr><th>Workflow</th><th>Version</th><th>Uses batch</th><th>Status</th><th>Paths</th><th>Endpoints using it</th><th>Saved by · when · note</th><th /></tr></thead>
            <tbody>
              {mine.map((f) => (
                <tr key={f.id} data-testid={`flow-row-${f.version}-${shortName(f.name)}`}>
                  <td className="mono"><b>{f.name}</b></td>
                  <td className="mono">{f.version}</td>
                  <td className="mono">{batchLabel(f, batches)}</td>
                  <td><FlowStatusBadge status={f.status} /></td>
                  <td>{f.pathsRun ? `${f.pathsRun.ok} / ${f.pathsRun.total}` : '—'}</td>
                  <td className="mono">{behind(f).join(', ') || '—'}</td>
                  <td>{f.savedBy.split('@')[0]} · {formatDateTime(f.savedAt)}{f.note ? ` · “${f.note}”` : ''}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1">
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => open(f)}>{isFlowEditable(f) ? 'Open' : 'View'}</button>
                      {f.status !== 'retired' && <Can permission="code.edit"><button type="button" className="btn btn-ghost btn-sm" onClick={() => open(store().newVersionFrom(f.id, user.email))}>New version from this</button></Can>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <NewWorkflowDrawer lender={lender} isOpen={creating} onClose={() => setCreating(false)} onCreated={(f) => open(f)} />
    </>
  )
}
